'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

class ContainerIterator {
  /**
   * @internal
   */
  constructor(iteratorType = 0 /* IteratorType.NORMAL */) {
    this.iteratorType = iteratorType;
  }
  /**
   * @param iter - The other iterator you want to compare.
   * @returns Whether this equals to obj.
   * @example
   * container.find(1).equals(container.end());
   */
  equals(iter) {
    return this._node === iter._node;
  }
}
class Base {
  constructor() {
    /**
     * @description Container's size.
     * @internal
     */
    this._length = 0;
  }
  /**
   * @returns The size of the container.
   * @example
   * const container = new Vector([1, 2]);
   * console.log(container.length); // 2
   */
  get length() {
    return this._length;
  }
  /**
   * @returns The size of the container.
   * @example
   * const container = new Vector([1, 2]);
   * console.log(container.size()); // 2
   */
  size() {
    return this._length;
  }
  /**
   * @returns Whether the container is empty.
   * @example
   * container.clear();
   * console.log(container.empty());  // true
   */
  empty() {
    return this._length === 0;
  }
}
class Container extends Base {}

class SequentialContainer extends Container {}

/**
 * @description Throw an iterator access error.
 * @internal
 */
function throwIteratorAccessError() {
  throw new RangeError('Iterator access denied!');
}

class RandomIterator extends ContainerIterator {
  /**
   * @internal
   */
  constructor(index, iteratorType) {
    super(iteratorType);
    this._node = index;
    if (this.iteratorType === 0 /* IteratorType.NORMAL */) {
      this.pre = function () {
        if (this._node === 0) {
          throwIteratorAccessError();
        }
        this._node -= 1;
        return this;
      };
      this.next = function () {
        if (this._node === this.container.size()) {
          throwIteratorAccessError();
        }
        this._node += 1;
        return this;
      };
    } else {
      this.pre = function () {
        if (this._node === this.container.size() - 1) {
          throwIteratorAccessError();
        }
        this._node += 1;
        return this;
      };
      this.next = function () {
        if (this._node === -1) {
          throwIteratorAccessError();
        }
        this._node -= 1;
        return this;
      };
    }
  }
  get pointer() {
    return this.container.getElementByPos(this._node);
  }
  set pointer(newValue) {
    this.container.setElementByPos(this._node, newValue);
  }
  isAccessible() {
    return this._node !== this.container.size();
  }
}

/**
 * @description Same to Math.ceil(a / b).
 * @param a - numerator.
 * @param b - Denominator.
 * @internal
 */
function ceil(a, b) {
  return Math.floor((a + b - 1) / b);
}
/**
 * @internal
 */
const floor = Math.floor;

class DequeIterator extends RandomIterator {
  constructor(node, container, iteratorType) {
    super(node, iteratorType);
    this.container = container;
  }
  copy() {
    return new DequeIterator(this._node, this.container, this.iteratorType);
  }
}
class Deque extends SequentialContainer {
  constructor(container = [], _bucketSize = 1 << 12) {
    super();
    /**
     * @internal
     */
    this._first = 0;
    /**
     * @internal
     */
    this._curFirst = 0;
    /**
     * @internal
     */
    this._last = 0;
    /**
     * @internal
     */
    this._curLast = 0;
    /**
     * @internal
     */
    this._bucketNum = 0;
    /**
     * @internal
     */
    this._map = [];
    const _length = (() => {
      if (typeof container.length === "number") return container.length;
      if (typeof container.size === "number") return container.size;
      if (typeof container.size === "function") return container.size();
      throw new TypeError("Cannot get the length or size of the container");
    })();
    this._bucketSize = _bucketSize;
    this._bucketNum = ceil(_length, this._bucketSize) || 1;
    for (let i = 0; i < this._bucketNum; ++i) {
      this._map.push(new Array(this._bucketSize));
    }
    const needBucketNum = ceil(_length, this._bucketSize);
    this._first = this._last = (this._bucketNum >> 1) - (needBucketNum >> 1);
    this._curFirst = this._curLast = this._bucketSize - _length % this._bucketSize >> 1;
    const self = this;
    container.forEach(function (element) {
      self.pushBack(element);
    });
  }
  /**
   * @description Growth the Deque.
   * @internal
   */
  _reAllocate(needBucketNum) {
    const newMap = [];
    const addBucketNum = needBucketNum || this._bucketNum >> 1 || 1;
    for (let i = 0; i < addBucketNum; ++i) {
      newMap[i] = new Array(this._bucketSize);
    }
    for (let i = this._first; i < this._bucketNum; ++i) {
      newMap[newMap.length] = this._map[i];
    }
    for (let i = 0; i < this._last; ++i) {
      newMap[newMap.length] = this._map[i];
    }
    newMap[newMap.length] = [...this._map[this._last]];
    this._first = addBucketNum;
    this._last = newMap.length - 1;
    for (let i = 0; i < addBucketNum; ++i) {
      newMap[newMap.length] = new Array(this._bucketSize);
    }
    this._map = newMap;
    this._bucketNum = newMap.length;
  }
  /**
   * @description Get the bucket position of the element and the pointer position by index.
   * @param pos - The element's index.
   * @internal
   */
  _getElementIndex(pos) {
    let curNodeBucketIndex, curNodePointerIndex;
    const index = this._curFirst + pos;
    curNodeBucketIndex = this._first + floor(index / this._bucketSize);
    if (curNodeBucketIndex >= this._bucketNum) {
      curNodeBucketIndex -= this._bucketNum;
    }
    curNodePointerIndex = (index + 1) % this._bucketSize - 1;
    if (curNodePointerIndex < 0) {
      curNodePointerIndex = this._bucketSize - 1;
    }
    return {
      curNodeBucketIndex,
      curNodePointerIndex
    };
  }
  clear() {
    this._map = [new Array(this._bucketSize)];
    this._bucketNum = 1;
    this._first = this._last = this._length = 0;
    this._curFirst = this._curLast = this._bucketSize >> 1;
  }
  begin() {
    return new DequeIterator(0, this);
  }
  end() {
    return new DequeIterator(this._length, this);
  }
  rBegin() {
    return new DequeIterator(this._length - 1, this, 1 /* IteratorType.REVERSE */);
  }

  rEnd() {
    return new DequeIterator(-1, this, 1 /* IteratorType.REVERSE */);
  }

  front() {
    if (this._length === 0) return;
    return this._map[this._first][this._curFirst];
  }
  back() {
    if (this._length === 0) return;
    return this._map[this._last][this._curLast];
  }
  pushBack(element) {
    if (this._length) {
      if (this._curLast < this._bucketSize - 1) {
        this._curLast += 1;
      } else if (this._last < this._bucketNum - 1) {
        this._last += 1;
        this._curLast = 0;
      } else {
        this._last = 0;
        this._curLast = 0;
      }
      if (this._last === this._first && this._curLast === this._curFirst) this._reAllocate();
    }
    this._length += 1;
    this._map[this._last][this._curLast] = element;
    return this._length;
  }
  popBack() {
    if (this._length === 0) return;
    const value = this._map[this._last][this._curLast];
    if (this._length !== 1) {
      if (this._curLast > 0) {
        this._curLast -= 1;
      } else if (this._last > 0) {
        this._last -= 1;
        this._curLast = this._bucketSize - 1;
      } else {
        this._last = this._bucketNum - 1;
        this._curLast = this._bucketSize - 1;
      }
    }
    this._length -= 1;
    return value;
  }
  /**
   * @description Push the element to the front.
   * @param element - The element you want to push.
   * @returns The size of queue after pushing.
   */
  pushFront(element) {
    if (this._length) {
      if (this._curFirst > 0) {
        this._curFirst -= 1;
      } else if (this._first > 0) {
        this._first -= 1;
        this._curFirst = this._bucketSize - 1;
      } else {
        this._first = this._bucketNum - 1;
        this._curFirst = this._bucketSize - 1;
      }
      if (this._first === this._last && this._curFirst === this._curLast) this._reAllocate();
    }
    this._length += 1;
    this._map[this._first][this._curFirst] = element;
    return this._length;
  }
  /**
   * @description Remove the _first element.
   * @returns The element you popped.
   */
  popFront() {
    if (this._length === 0) return;
    const value = this._map[this._first][this._curFirst];
    if (this._length !== 1) {
      if (this._curFirst < this._bucketSize - 1) {
        this._curFirst += 1;
      } else if (this._first < this._bucketNum - 1) {
        this._first += 1;
        this._curFirst = 0;
      } else {
        this._first = 0;
        this._curFirst = 0;
      }
    }
    this._length -= 1;
    return value;
  }
  getElementByPos(pos) {
    if (pos < 0 || pos > this._length - 1) {
      throw new RangeError();
    }
    const {
      curNodeBucketIndex,
      curNodePointerIndex
    } = this._getElementIndex(pos);
    return this._map[curNodeBucketIndex][curNodePointerIndex];
  }
  setElementByPos(pos, element) {
    if (pos < 0 || pos > this._length - 1) {
      throw new RangeError();
    }
    const {
      curNodeBucketIndex,
      curNodePointerIndex
    } = this._getElementIndex(pos);
    this._map[curNodeBucketIndex][curNodePointerIndex] = element;
  }
  insert(pos, element, num = 1) {
    const length = this._length;
    if (pos < 0 || pos > length) {
      throw new RangeError();
    }
    if (pos === 0) {
      while (num--) this.pushFront(element);
    } else if (pos === this._length) {
      while (num--) this.pushBack(element);
    } else {
      const arr = [];
      for (let i = pos; i < this._length; ++i) {
        arr.push(this.getElementByPos(i));
      }
      this.cut(pos - 1);
      for (let i = 0; i < num; ++i) this.pushBack(element);
      for (let i = 0; i < arr.length; ++i) this.pushBack(arr[i]);
    }
    return this._length;
  }
  /**
   * @description Remove all elements after the specified position (excluding the specified position).
   * @param pos - The previous position of the first removed element.
   * @returns The size of the container after cutting.
   * @example
   * deque.cut(1); // Then deque's size will be 2. deque -> [0, 1]
   */
  cut(pos) {
    if (pos < 0) {
      this.clear();
      return 0;
    }
    const {
      curNodeBucketIndex,
      curNodePointerIndex
    } = this._getElementIndex(pos);
    this._last = curNodeBucketIndex;
    this._curLast = curNodePointerIndex;
    this._length = pos + 1;
    return this._length;
  }
  eraseElementByPos(pos) {
    if (pos < 0 || pos > this._length - 1) {
      throw new RangeError();
    }
    if (pos === 0) this.popFront();else if (pos === this._length - 1) this.popBack();else {
      const length = this._length - 1;
      let {
        curNodeBucketIndex: curBucket,
        curNodePointerIndex: curPointer
      } = this._getElementIndex(pos);
      for (let i = pos; i < length; ++i) {
        const {
          curNodeBucketIndex: nextBucket,
          curNodePointerIndex: nextPointer
        } = this._getElementIndex(pos + 1);
        this._map[curBucket][curPointer] = this._map[nextBucket][nextPointer];
        curBucket = nextBucket;
        curPointer = nextPointer;
      }
      this.popBack();
    }
    return this._length;
  }
  eraseElementByValue(value) {
    const length = this._length;
    if (length === 0) return 0;
    let i = 0;
    let index = 0;
    while (i < length) {
      const element = this.getElementByPos(i);
      if (element !== value) {
        this.setElementByPos(index, element);
        index += 1;
      }
      i += 1;
    }
    this.cut(index - 1);
    return this._length;
  }
  eraseElementByIterator(iter) {
    const _node = iter._node;
    this.eraseElementByPos(_node);
    iter = iter.next();
    return iter;
  }
  find(element) {
    for (let i = 0; i < this._length; ++i) {
      if (this.getElementByPos(i) === element) {
        return new DequeIterator(i, this);
      }
    }
    return this.end();
  }
  reverse() {
    this._map.reverse().forEach(function (bucket) {
      bucket.reverse();
    });
    const {
      _first,
      _last,
      _curFirst,
      _curLast
    } = this;
    this._first = this._bucketNum - _last - 1;
    this._last = this._bucketNum - _first - 1;
    this._curFirst = this._bucketSize - _curLast - 1;
    this._curLast = this._bucketSize - _curFirst - 1;
    return this;
  }
  unique() {
    if (this._length <= 1) {
      return this._length;
    }
    let index = 1;
    let pre = this.getElementByPos(0);
    for (let i = 1; i < this._length; ++i) {
      const cur = this.getElementByPos(i);
      if (cur !== pre) {
        pre = cur;
        this.setElementByPos(index++, cur);
      }
    }
    this.cut(index - 1);
    return this._length;
  }
  sort(cmp) {
    const arr = [];
    for (let i = 0; i < this._length; ++i) {
      arr.push(this.getElementByPos(i));
    }
    arr.sort(cmp);
    for (let i = 0; i < this._length; ++i) {
      this.setElementByPos(i, arr[i]);
    }
    return this;
  }
  /**
   * @description Remove as much useless space as possible.
   */
  shrinkToFit() {
    if (this._length === 0) return;
    const newMap = [];
    if (this._first === this._last) return;else if (this._first < this._last) {
      for (let i = this._first; i <= this._last; ++i) {
        newMap.push(this._map[i]);
      }
    } else {
      for (let i = this._first; i < this._bucketNum; ++i) {
        newMap.push(this._map[i]);
      }
      for (let i = 0; i <= this._last; ++i) {
        newMap.push(this._map[i]);
      }
    }
    this._first = 0;
    this._last = newMap.length - 1;
    this._map = newMap;
  }
  forEach(callback) {
    for (let i = 0; i < this._length; ++i) {
      callback(this.getElementByPos(i), i, this);
    }
  }
  *[Symbol.iterator]() {
    for (let i = 0; i < this._length; ++i) {
      yield this.getElementByPos(i);
    }
  }
}

exports.Deque = Deque;
//# sourceMappingURL=index.js.map
